import { Component, Inject, Injector } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout-external-settings';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { IGridSettings } from '@common-modules/shared/constants/grid.constants';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { MapStep } from '@common-modules/wlm-map/models/map-step';
import { SelectedLayerInfo } from '@common-modules/wlm-map/models/selected-layer-info';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  AttributesChartConfiguration,
  AttributesChartPopup,
} from '../attributes-chart-popup/attributes-chart-popup.component';
import { CategoryDto } from '../models/category.dto';
import { GridAttributeValue } from '../models/grid-attribute-value';
import { MasterOperatorFields } from '../models/master-operator-fields.enum';
import { ScenarioInfoService } from '../services/scenario-info.service';

const COMPONENT_SELECTOR = 'tc-master-operator-attribute-widget';

@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-attribute-widget.component.html',
    styleUrls: ['./master-operator-attribute-widget.component.scss'],
    providers: [ReduxStateService],
    standalone: false
})
export class MasterOperatorAttributeWidgetComponent extends BaseDynamicWidgetComponent {
  widgetInstanceKey: string;
  gridSettings: IGridSettings;
  gridAttributeValues: GridAttributeValue[];

  selectedLayer: SelectedLayerInfo;
  instanceId: string;
  category: CategoryDto;
  attributes$: Subscription;

  selectedAttributes: GridAttributeValue[];

  private _mapStep: MapStep;

  public get mapStep(): MapStep {
    return this._mapStep;
  }
  public set mapStep(v: MapStep) {
    this._mapStep = v;
    if (v) {
      this.loadLayerAttributes(v.step);
    }
  }

  isLoading = false;

  parentWidgetInstanceKey: string;

  layoutSettings: DynamicLayoutSettings;

  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;

  get componentName(): string {
    return 'MasterOperatorAttributeWidgetComponent';
  }

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private _scenarioInfoService: ScenarioInfoService,
    private _attributePopup: MatDialog,
    private _localization: LocalizationHelperService
  ) {
    super(injector, widgetSettings);
    this.widgetInstanceKey = widgetSettings.widgetInstanceKey;
    this.selectedLayer = widgetSettings.params.selectedLayer;
    this.category = widgetSettings.params.category;
    this.parentWidgetInstanceKey = this.selectedLayer.parentKey;
  }

  onWidgetInit(): void {
    this.gridSettings = this.getGridSettings();
    this.loadLayerAttributes(this.selectedLayer.step);

    this._state
      .select<MapStep>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.MapStep,
        })
      )
      .pipe(
        untilDestroyed(this),
        filter((data) => {
          return !!data && data.parentKey == this.parentWidgetInstanceKey;
        })
      )
      .subscribe((value) => {
        this.mapStep = value;
      });
  }

  openAttributesPopup() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '865px';
    dialogConfig.height = '550px';
    const startDate = this.selectedLayer.startDate;
    const endDate = this.selectedLayer.endDate;

    const chartParameters = new AttributesChartConfiguration({
      startDate: startDate,
      endDate: endDate,
      scenarioId: this.selectedLayer.scenarioId,
      snapshotId: this.selectedLayer.snapshotId,
      networkElementId: this.selectedLayer.layerId,
      category: this.category,
      attributeNames: this.selectedAttributes.map((x) => x.name),
    });

    dialogConfig.data = chartParameters;
    this._attributePopup.open(AttributesChartPopup, dialogConfig);
  }

  private loadLayerAttributes(step: Date) {
    if (this.attributes$) {
      this.attributes$.unsubscribe();
    }

    this.isLoading = true;
    this.attributes$ = this._scenarioInfoService
      .getLayerAttributes(
        this.selectedLayer.snapshotId,
        this.selectedLayer.scenarioId,
        this.selectedLayer.layerId,
        step,
        this.category.name
      )
      .pipe(
        map((attrValues) => {
          return attrValues.map((attrValue) => {
            const attrInfo = this.category.attributes.find((x) => x.name == attrValue.name);
            const attributeValue = new GridAttributeValue({ ...attrInfo, ...attrValue });
            const { value, format, units } = attributeValue;
            attributeValue.formattedValue = this._localization.formatAttributeValue(
              value,
              format,
              units
            );
            return attributeValue;
          });
        })
      )
      .subscribe({
        next: (gridAttributeValues) => {
          this.gridAttributeValues = gridAttributeValues;
          this.isLoading = false;
          this.attributes$ = null;
        },
        error: (error) => {
          this.isLoading = false;
          this.attributes$ = null;
        },
      });
  }

  private getGridSettings(): IGridSettings {
    return {
      count: true,
      pageSize: 10,
      sortable: true,
      filterable: 'menu',
      ignoreCase: true,
      resizable: true,
      reorderable: true,
      navigatable: false,
      selectable: { enabled: true, checkboxOnly: true },
      showZoneColumns: false,
      showSelectAllColumn: true,
      pageable: { type: 'input', pageSizes: [10, 20, 50] },
      excelFileName: '',
      gridColumnSettings: [
        {
          field: 'alias',
          title: 'Attribute Name',
          localeTitle: { 'en-GB': 'Attribute Name' },
          type: 'text',
          uniqueName: 'alias',
          width: 220,
          visible: true,
        },
        {
          field: 'formattedValue',
          title: 'Attribute Value',
          localeTitle: { 'en-GB': 'Attribute Value' },
          type: 'text',
          uniqueName: 'formattedValue',
          width: 220,
          visible: true,
        },
      ],
      selectByFieldName: 'name',
      enableAutoResize: false,
      selectionPersistency: false,
      sort: [{ field: 'name', dir: 'asc' }],
      buttons: { show: ['clear_grid_filters'] },
      useSmallPager: true,
    };
  }
}
